import type { MockOrder } from 'api/recoveryAi.types'

const MS_PER_DAY = 24 * 60 * 60 * 1000
// Yesterday: recent enough that "placed on <date>" reads plausibly, far enough
// back that the complaint isn't about an order from minutes ago.
const DAYS_BEFORE_TODAY = 1

const isoDate = (date: Date): string => date.toISOString().slice(0, 10)

/**
 * The order free chat anchors on when no guided persona is picked ("Test your own"
 * in the wizard, and the whole Agent Tuning tab). Without it those surfaces send
 * `"{}"`, thanx-ai's Bouncer computes a zero anchor, and the turn escalates as
 * `no_priced_items` before the Appraiser runs — so a resolution was unreachable.
 *
 *   context: every field here is substituted VERBATIM into the Talker's system
 *   prompt (recovery_talker_system_prompt.md:13,20 via prompts/builders/
 *   recovery_prompt_builder.rb:41) — "You are discussing order {{order_id}} placed
 *   on {{order_date}} at {{location_name}}", and the prompt instructs the agent at
 *   :37 to "use the customer's first name naturally". So these are not inert
 *   fixture values: they are things the agent will say to the merchant. No internal
 *   slugs, and no frozen date (which would recede forever).
 *   context: item prices carry the anchor — MockOrder models no price_breakdown, so
 *   do not drop `price_cents`. The anchor also sets the reward cost cap.
 *   freeChatOrder.test.ts pins both, and records the Bouncer's qty/quantity mismatch.
 *
 * `now` is injectable so tests pin the date without faking timers.
 */
export const buildFreeChatOrder = (now: Date = new Date()): MockOrder => ({
  // Merchant-plausible, matching the ORD- convention personaScenarios.ts uses.
  // NOT a sandbox slug — the agent can quote this back verbatim.
  order_id: 'ORD-100482',
  location_name: 'Main Location',
  order_date: isoDate(new Date(now.getTime() - DAYS_BEFORE_TODAY * MS_PER_DAY)),
  order_total_cents: 2800,
  items: [
    {
      id: 'free-chat-1',
      name: 'Entree',
      quantity: 1,
      price_cents: 1800,
    },
    {
      id: 'free-chat-2',
      name: 'Side',
      quantity: 1,
      price_cents: 1000,
    },
  ],
  delivery_method: 'pickup',
  // Kept, deliberately: the merchant role-plays a customer here, and the guided
  // personas carry names too, so a nameless order would be the odd one out. The
  // open design question is surfacing this order in the UI, not removing the name.
  customer_first_name: 'Alex',
})

// Cache key/value for getFreeChatOrder — module-level so the cache survives
// across calls without becoming a React state/ref concern.
let cachedDay: string | null = null
let cachedOrder: MockOrder | null = null

/**
 * The SAME free-chat order, shared for as long as `order_date` (yesterday,
 * UTC) stays current. `buildFreeChatOrder()` returns a fresh object every
 * call, which is fine for a one-off render but wrong for anything that
 * memoizes or diffs by reference — useSimulatorData's `conversationContext`
 * builder and SimulatorEngine's OrderDetailsCard both need the SAME order the
 * AI is actually reasoning over, and a fresh object each render would defeat
 * identity-based memoization for no behavioral gain (the date only needs
 * day-granularity freshness, not per-render freshness).
 *
 * A plain module-load-once constant (the prior approach) froze `order_date`
 * at whatever moment the bundle first evaluated this module — for a sandbox
 * tab left open across a UTC midnight, that date recedes further into the
 * past every day the tab stays open, contradicting the "never recedes"
 * intent documented on `buildFreeChatOrder`. This resolver re-derives the
 * order once per UTC day (cache keyed on today's ISO date) and returns the
 * SAME reference for every call within that day, so identity-based
 * memoization still holds intra-day while the date itself never goes stale.
 *
 * Call this everywhere free chat needs its stand-in order; keep
 * `buildFreeChatOrder` itself around only for tests that pin a specific `now`.
 */
export const getFreeChatOrder = (now: Date = new Date()): MockOrder => {
  const today = isoDate(now)
  if (cachedOrder === null || cachedDay !== today) {
    cachedDay = today
    cachedOrder = buildFreeChatOrder(now)
  }
  return cachedOrder
}
